import jsPDF from 'jspdf';

export const generateDrivingSchoolCarsQuote = async (formData) => {
  try {
    // Calculate values
    const sumInsured = parseFloat(formData.sumInsured);
    const rate = 0.05; // 5.00%
    const premium = sumInsured * rate;
    const subtotal = premium;
    const levies = premium * 0.0045; // 0.45% of premium
    const stampDuty = 40;
    const totalPremium = subtotal + levies + stampDuty;
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);

    // Common insurance text items
    const textItems = [
      { text: 'This quotation is valid for 30 days from the date of issue.' },
      { text: 'Cover is subject to payment of premium and acceptance of proposal form.' },
      { text: 'All claims must be reported within 24 hours of occurrence.' },
      { text: 'Terms and conditions apply as per policy document.' }
    ];

    // Generate PDF
    await generatePDF(formData, {
      sumInsured,
      rate,
      premium,
      subtotal,
      levies,
      stampDuty,
      totalPremium,
      timestamp,
      textItems
    });

  } catch (error) {
    console.error('Error generating Driving School Cars quote:', error);
    throw error;
  }
};

const generatePDF = async (formData, calculations) => {
  const doc = new jsPDF();
  
  // Load images
  const logo = await loadImage('/logo.png');
  const stamp = await loadImage('/stamp.jpg');
  
  // Add header background
  doc.setFillColor(15, 79, 72);
  doc.rect(0, 0, 210, 40, 'F');
  
  // Add logo in header (centered)
  if (logo) {
    doc.addImage(logo, 'PNG', 65, 5, 80, 40);
  }
  
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(12);
  doc.text('Driving School Cars Insurance Quotation', 105, 35, { align: 'center' });
  
  // Reset text color
  doc.setTextColor(0, 0, 0);
  
  // Add content
  doc.setFontSize(11);
  let yPos = 50;
  
  // Vehicle Details
  doc.setFont(undefined, 'bold');
  doc.text('Vehicle Registration:', 20, yPos);
  doc.setFont(undefined, 'normal');
  doc.text(formData.vehicleRegistration, 80, yPos);
  
  // Premium Breakdown Table
  yPos += 20;
  const tableData = [
    ['Sum Insured', calculations.sumInsured.toLocaleString('en-KE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })],
    ['Rate', '5.00%'],
    ['Premium', calculations.premium.toLocaleString('en-KE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })],
    ['Subtotal', calculations.subtotal.toLocaleString('en-KE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })],
    ['Training Levy (0.45%)', calculations.levies.toLocaleString('en-KE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })],
    ['Stamp Duty', calculations.stampDuty.toLocaleString('en-KE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })],
    ['Total Premium', calculations.totalPremium.toLocaleString('en-KE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })]
  ];

  // Draw manual table
  let tableY = yPos;
  const leftMargin = 20;
  const colWidth1 = 100;
  const colWidth2 = 70;
  const rowHeight = 8;

  // Table header
  doc.setFillColor(15, 79, 72);
  doc.rect(leftMargin, tableY, colWidth1 + colWidth2, rowHeight, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(10);
  doc.setFont(undefined, 'bold');
  doc.text('Description', leftMargin + 2, tableY + 6);
  doc.text('Amount (KES)', leftMargin + colWidth1 + colWidth2 - 2, tableY + 6, { align: 'right' });

  // Table body
  tableY += rowHeight;
  doc.setTextColor(0, 0, 0);
  doc.setFont(undefined, 'normal');

  tableData.forEach((row, index) => {
    if (index % 2 === 0) {
      doc.setFillColor(245, 245, 245);
      doc.rect(leftMargin, tableY, colWidth1 + colWidth2, rowHeight, 'F');
    }
    doc.text(row[0], leftMargin + 2, tableY + 6);
    doc.text(row[1], leftMargin + colWidth1 + colWidth2 - 2, tableY + 6, { align: 'right' });
    tableY += rowHeight;
  });

  // Draw table border
  doc.setDrawColor(200, 200, 200);
  doc.rect(leftMargin, yPos, colWidth1 + colWidth2, (tableData.length + 1) * rowHeight);
  
  // Footer
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(9);
    doc.setTextColor(128);
    doc.text(
      'Prepared By: New Digital Portal',
      105,
      doc.internal.pageSize.height - 10,
      { align: 'center' }
    );
  }
  
  // Add additional text items
  if (calculations.textItems && calculations.textItems.length > 0) {
    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    let textY = doc.internal.pageSize.height - 75;
    calculations.textItems.forEach(item => {
      doc.text(item.text, 20, textY);
      textY += 4;
    });
  }
  
  // Generate timestamp for filename
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
  const fileName = `Driving_School_Cars_Quote_${formData.vehicleRegistration.replace(/\s/g, '_')}_${timestamp}.pdf`;
  
  // Add approval section at bottom of last page
  const bottomY = doc.internal.pageSize.height - 50;
  doc.setDrawColor(0);
  doc.line(20, bottomY, 190, bottomY);

  doc.setFontSize(10);
  doc.setFont(undefined, 'bold');
  doc.text('Approved by: K. Nyamu', 20, bottomY + 10);
  doc.text('Head of Department', 20, bottomY + 18);
  
  // Add stamp at bottom right with approval section
  if (stamp) {
    doc.addImage(stamp, 'JPEG', 155, doc.internal.pageSize.height - 55, 35, 25);
  }
  
  doc.save(fileName);
};

// Helper function to load images
const loadImage = (url) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = url;
  });
};
